import Choices from 'choices.js';
import ChevronDownIcon from '../../assets/icons/chevron-down.svg';

interface DropdownSettings {
    prefixIcon?: string;
    ariaLabel?: string;
}

interface DropdownProps {
    el: HTMLElement;
    props: DropdownSettings;
}

interface CustomSelectElProps {
    className: string;
    id: string;
    value: string;
    active: boolean;
    disabled: boolean;
    label: string;
    icon?: string;
    iconString?: boolean;
}

const selectEl = ({ className, id, value, active, disabled, label, icon, iconString }: CustomSelectElProps): string => {
    const iconRender = (): string => {
        if (!icon) {
            return '';
        }

        if (iconString) {
            return icon;
        }

        return `<img class="choices__item__icon" src="${icon}">`;
    };

    return `
    <div
        role="option"
        class="${className}"
        data-item
        data-id="${id}"
        data-value="${value}"
        ${active ? 'aria-selected="true"' : ''}
        ${disabled ? 'aria-disabled="true"' : ''}  
    >
        ${iconRender()}
         <span class="choices__item__text">${label}</span>
         ${ChevronDownIcon}
    </div>
`;
};

interface CustomOptionElProps {
    className: string;
    id: string;
    value: string;
    groupId: number;
    itemSelectText: string;
    disabled: boolean;
    label: string;
    icon?: string;
    defaultIcon?: boolean;
    selected: boolean;
}

const optionEl = ({ className, id, value, itemSelectText, disabled, label, groupId, icon }: CustomOptionElProps): string => {
    return `
    <div
        class="${className}"
        data-choice
        data-id="${id}"
        data-value="${value}"
        data-select-text="${itemSelectText}"
        ${disabled ? 'data-choice-disabled aria-disabled="true"' : 'data-choice-selectable'}
        ${groupId > 0 ? 'role="treeitem"' : 'role="option"'}
    >
        ${icon ? `<img class="choices__item__icon" src="${icon}">` : ''}
        <span class="choices__item__text">${label}</span>
    </div>
`;
};


const Dropdown = ({ el, props }: DropdownProps): Choices => {
    const { prefixIcon, ariaLabel } = props;

    const choices = new Choices(el, {
        placeholder: false,
        searchEnabled: false,
        shouldSort: false,
        itemSelectText: '',
        // choices: choicesItems,

        // wrongly configured typescript plugin as void so we have to follow
        callbackOnCreateTemplates(strToEl): void {
            const { itemSelectText } = (this as Choices).config;

            // not set properly by the plugin thus need to disable it
            /* eslint-disable */
            return {
                containerOuter(
                    { classNames: { containerOuter } }: any,
                    dir: HTMLElement['dir'],
                    isSelectElement: boolean,
                    isSelectOneElement: boolean,
                    searchEnabled: boolean,
                    passedElementType: any,
                    labelId: string,
                ): HTMLDivElement {
                    const div = Object.assign(document.createElement('div'), {
                        className: containerOuter,
                    });

                    div.dataset.type = passedElementType;

                    if (dir) {
                        div.dir = dir;
                    }

                    if (isSelectOneElement) {
                        div.tabIndex = 0;
                    }

                    if (isSelectElement) {
                        //   div.setAttribute('role', searchEnabled ? 'combobox' : 'listbox');
                        if (searchEnabled) {
                            div.setAttribute('aria-autocomplete', 'list');
                        }
                    }

                    // div.setAttribute('aria-haspopup', 'true');
                    // div.setAttribute('aria-expanded', 'false');
                    if (labelId) {
                        div.setAttribute('aria-labelledby', labelId);
                    }

                    return div;
                },
                itemList(
                    { classNames: { list, listSingle, listItems } }: any,
                    isSelectOneElement: boolean,
                ): HTMLDivElement {
                    const itemListDiv = Object.assign(document.createElement('div'), {
                        className: `${list} ${isSelectOneElement ? listSingle : listItems}`,
                    });

                    itemListDiv.setAttribute('role', 'listbox');
                    if (ariaLabel) {
                        itemListDiv.setAttribute('aria-label', ariaLabel);
                    }

                    return itemListDiv;
                },
                item({ classNames }: any, { highlighted, id, label, active, disabled, value, customProperties }: any): HTMLElement {
                    return strToEl(selectEl({
                        className: `${String(classNames.item)} ${String(
                            highlighted
                                ? classNames.highlightedState
                                : classNames.itemSelectable
                        )}`,
                        id: String(id),
                        label: String(label),
                        value: String(value),
                        active,
                        disabled,
                        icon: prefixIcon ? prefixIcon : customProperties.iconUrl,
                        iconString: !!prefixIcon
                    }));
                },
                choice({ classNames }: any, { id, label, disabled, value, groupId, customProperties, selected }: any) {
                    const placeholderClass = value === '' ? 'choices__placeholder' : '';

                    return strToEl(optionEl({
                        className: `${String(classNames.item)} ${String(classNames.itemChoice)} ${placeholderClass} ${String(
                            disabled
                                ? classNames.itemDisabled
                                : classNames.itemSelectable
                        )}`,
                        id: String(id),
                        label: String(label),
                        value: String(value),
                        itemSelectText: itemSelectText ? String(itemSelectText) : '',
                        groupId,
                        disabled,
                        selected,
                        icon: customProperties.iconUrl
                    }));
                }
                // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
            } as unknown as void;
            /* eslint-enable */
        },
    });

    return choices;
};

export default Dropdown;
